"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.DataTapeField = void 0;
const Bank = __importStar(require("../Bank"));
const Utils = __importStar(require("../Utils"));
const zod_1 = require("zod");
// this compile time assert ensures that a function is exhaustive
// useful to use in your default case for switch statement.
const assertUnreachable = (x) => {
    throw new Error(`finleyAssertUnreachable: ${x}`);
};
/**
 * ============= DATA TAPE FIELD =============
 */
var DataTapeField;
(function (DataTapeField) {
    DataTapeField.NumericTypes = [
        'currency',
        'float',
        'integer',
        'percentage',
        'optionalCurrency',
        'optionalFloat',
        'optionalInteger',
        'optionalPercentage',
    ];
    DataTapeField.ValidTypes = [
        'boolean',
        'date',
        'dateTime',
        'string',
        'optionalBoolean',
        'optionalDate',
        'optionalDateTime',
        'optionalString',
        ...DataTapeField.NumericTypes,
    ];
    DataTapeField.OptionalTypes = [
        'optionalCurrency',
        'optionalFloat',
        'optionalInteger',
        'optionalPercentage',
        'optionalBoolean',
        'optionalDate',
        'optionalDateTime',
        'optionalString',
    ];
    DataTapeField.isOptionalType = (type) => DataTapeField.OptionalTypes.includes(type);
    DataTapeField.ValidTypesSchema = zod_1.z.enum(DataTapeField.ValidTypes);
    DataTapeField.makeOptionalType = (type) => {
        if (DataTapeField.isOptionalType(type)) {
            return type;
        }
        switch (type) {
            case 'currency':
                return 'optionalCurrency';
            case 'float':
                return 'optionalFloat';
            case 'integer':
                return 'optionalInteger';
            case 'percentage':
                return 'optionalPercentage';
            case 'boolean':
                return 'optionalBoolean';
            case 'date':
                return 'optionalDate';
            case 'dateTime':
                return 'optionalDateTime';
            case 'string':
                return 'optionalString';
            default:
                return assertUnreachable(type);
        }
    };
    // Common config parts
    const AbstractConfigSchema = zod_1.z.object({
        field: zod_1.z.string().describe('This field name in the tape'),
        columnToReadFrom: zod_1.z
            .string()
            .optional()
            .describe('Optional source column override'),
        description: zod_1.z.string().optional().describe('Customer-facing description'),
    });
    const RequiredFieldConfigSchema = AbstractConfigSchema.extend({
        defaultTo: zod_1.z
            .string()
            .optional()
            .describe('Default value if the field is null (required fields only)'),
    });
    const OptionalFieldConfigSchema = AbstractConfigSchema.extend({
        externalNullValues: zod_1.z
            .array(zod_1.z.string())
            .optional()
            .describe('Values to treat as null (optional fields only)'),
    });
    // Boolean
    DataTapeField.BooleanConfigSchema = RequiredFieldConfigSchema.extend({
        externalBooleanTrue: zod_1.z.string().describe('CSV string mapping to true'),
        type: zod_1.z.literal('boolean'),
    });
    DataTapeField.OptionalBooleanConfigSchema = OptionalFieldConfigSchema.extend({
        externalBooleanTrue: zod_1.z.string().describe('CSV string mapping to true'),
        type: zod_1.z.literal('optionalBoolean'),
    });
    // Currency
    DataTapeField.CurrencyConfigSchema = RequiredFieldConfigSchema.extend({
        currencyCode: Bank.CurrencyCodeSchema.describe('Currency code'),
        type: zod_1.z.literal('currency'),
    });
    DataTapeField.OptionalCurrencyConfigSchema = OptionalFieldConfigSchema.extend({
        currencyCode: Bank.CurrencyCodeSchema.describe('Currency code'),
        type: zod_1.z.literal('optionalCurrency'),
    });
    // Date
    DataTapeField.DateConfigSchema = RequiredFieldConfigSchema.extend({
        externalDateFormat: zod_1.z.string().describe('Date format string'),
        expectExcelFormatForSanitize: zod_1.z
            .boolean()
            .optional()
            .describe('Expect Excel serial date format for sanitization'),
        type: zod_1.z.literal('date'),
    });
    DataTapeField.OptionalDateConfigSchema = OptionalFieldConfigSchema.extend({
        externalDateFormat: zod_1.z.string().describe('Date format string'),
        expectExcelFormatForSanitize: zod_1.z
            .boolean()
            .optional()
            .describe('Expect Excel serial date format for sanitization'),
        type: zod_1.z.literal('optionalDate'),
    });
    // DateTime
    DataTapeField.DateTimeConfigSchema = RequiredFieldConfigSchema.extend({
        externalDateTimeFormat: zod_1.z.string().describe('DateTime format string'),
        defaultTz: zod_1.z.nativeEnum(Utils.DateTimeZones).describe('Default timezone'),
        type: zod_1.z.literal('dateTime'),
    });
    DataTapeField.OptionalDateTimeConfigSchema = OptionalFieldConfigSchema.extend({
        externalDateTimeFormat: zod_1.z.string().describe('DateTime format string'),
        defaultTz: zod_1.z.nativeEnum(Utils.DateTimeZones).describe('Default timezone'),
        type: zod_1.z.literal('optionalDateTime'),
    });
    // Float, Integer, Percentage
    DataTapeField.FloatConfigSchema = RequiredFieldConfigSchema.extend({
        type: zod_1.z.literal('float'),
    });
    DataTapeField.OptionalFloatConfigSchema = OptionalFieldConfigSchema.extend({
        type: zod_1.z.literal('optionalFloat'),
    });
    DataTapeField.IntegerConfigSchema = RequiredFieldConfigSchema.extend({
        type: zod_1.z.literal('integer'),
    });
    DataTapeField.OptionalIntegerConfigSchema = OptionalFieldConfigSchema.extend({
        type: zod_1.z.literal('optionalInteger'),
    });
    DataTapeField.PercentageConfigSchema = RequiredFieldConfigSchema.extend({
        type: zod_1.z.literal('percentage'),
    });
    DataTapeField.OptionalPercentageConfigSchema = OptionalFieldConfigSchema.extend({
        type: zod_1.z.literal('optionalPercentage'),
    });
    // String
    DataTapeField.StringConfigSchema = RequiredFieldConfigSchema.extend({
        type: zod_1.z.literal('string'),
    });
    DataTapeField.OptionalStringConfigSchema = OptionalFieldConfigSchema.extend({
        type: zod_1.z.literal('optionalString'),
    });
    // we need to expose these schemas for
    // extending these configs individually outside this file
    DataTapeField.AllBaseConfigSchemas = [
        DataTapeField.BooleanConfigSchema,
        DataTapeField.OptionalBooleanConfigSchema,
        DataTapeField.CurrencyConfigSchema,
        DataTapeField.OptionalCurrencyConfigSchema,
        DataTapeField.DateConfigSchema,
        DataTapeField.OptionalDateConfigSchema,
        DataTapeField.DateTimeConfigSchema,
        DataTapeField.OptionalDateTimeConfigSchema,
        DataTapeField.FloatConfigSchema,
        DataTapeField.OptionalFloatConfigSchema,
        DataTapeField.IntegerConfigSchema,
        DataTapeField.OptionalIntegerConfigSchema,
        DataTapeField.PercentageConfigSchema,
        DataTapeField.OptionalPercentageConfigSchema,
        DataTapeField.StringConfigSchema,
        DataTapeField.OptionalStringConfigSchema,
    ];
    // Discriminated union of all configs
    DataTapeField.BaseConfigSchema = zod_1.z
        .discriminatedUnion('type', DataTapeField.AllBaseConfigSchemas)
        .describe('Union of all DataTapeField config types');
})(DataTapeField || (exports.DataTapeField = DataTapeField = {}));
//# sourceMappingURL=DataTapeField.js.map